export enum RoleID {
  ADMIN = 1,
  HEAD_OF_EDUCATION = 2,
  ACADEMIC_OFFICER = 3,
  FINANCE_OFFICER = 4,
  DOCUMENT_OFFICER = 5,
  HUMAN_RESOURCE = 6,
  TEACHER = 7,
  HEAD_OF_DEPARTMENT = 8,
  VICE_DEAN_ADMINISTRATION = 9,
  VICE_DEAN_ACADEMIC = 10,
}

export enum RoleCode {
  ADMIN = "admin",
  HEAD_OF_EDUCATION = "head_of_education",
  ACADEMIC_OFFICER = "academic_officer",
  FINANCE_OFFICER = "finance_officer",
  DOCUMENT_OFFICER = "document_officer",
  HUMAN_RESOURCE = "human_resource",
  TEACHER = "teacher",
  HEAD_OF_DEPARTMENT = "head_of_department",
  VICE_DEAN_ADMINISTRATION = "vice_dean_administration",
  VICE_DEAN_ACADEMIC = "vice_dean_academic",
}

export interface RoleOption {
  id: RoleID;
  code: RoleCode;
  name: RoleCode;
  name_th: string;
  name_en: string;
  label: string;
}

export const ROLE_OPTIONS: RoleOption[] = [
  {
    id: RoleID.ADMIN,
    code: RoleCode.ADMIN,
    name: RoleCode.ADMIN,
    name_th: "แอดมิน",
    name_en: "Admin",
    label: "แอดมิน",
  },
  {
    id: RoleID.HEAD_OF_EDUCATION,
    code: RoleCode.HEAD_OF_EDUCATION,
    name: RoleCode.HEAD_OF_EDUCATION,
    name_th: "หัวหน้างานการศึกษา",
    name_en: "Head of Education",
    label: "หัวหน้างานการศึกษา",
  },
  {
    id: RoleID.ACADEMIC_OFFICER,
    code: RoleCode.ACADEMIC_OFFICER,
    name: RoleCode.ACADEMIC_OFFICER,
    name_th: "เจ้าหน้าที่วิชาการ",
    name_en: "Academic Officer",
    label: "เจ้าหน้าที่วิชาการ",
  },
  {
    id: RoleID.FINANCE_OFFICER,
    code: RoleCode.FINANCE_OFFICER,
    name: RoleCode.FINANCE_OFFICER,
    name_th: "เจ้าหน้าที่การเงิน",
    name_en: "Finance Officer",
    label: "เจ้าหน้าที่การเงิน",
  },
  {
    id: RoleID.DOCUMENT_OFFICER,
    code: RoleCode.DOCUMENT_OFFICER,
    name: RoleCode.DOCUMENT_OFFICER,
    name_th: "เจ้าหน้าที่สารบรรณ",
    name_en: "Document Officer",
    label: "เจ้าหน้าที่สารบรรณ",
  },
  {
    id: RoleID.HUMAN_RESOURCE,
    code: RoleCode.HUMAN_RESOURCE,
    name: RoleCode.HUMAN_RESOURCE,
    name_th: "นักทรัพยากรบุคคล",
    name_en: "Human Resource Officer",
    label: "นักทรัพยากรบุคคล",
  },
  {
    id: RoleID.TEACHER,
    code: RoleCode.TEACHER,
    name: RoleCode.TEACHER,
    name_th: "อาจารย์",
    name_en: "Teacher",
    label: "อาจารย์",
  },
  {
    id: RoleID.HEAD_OF_DEPARTMENT,
    code: RoleCode.HEAD_OF_DEPARTMENT,
    name: RoleCode.HEAD_OF_DEPARTMENT,
    name_th: "หัวหน้าสาขาวิชา",
    name_en: "Head of Department",
    label: "หัวหน้าสาขาวิชา",
  },
  {
    id: RoleID.VICE_DEAN_ADMINISTRATION,
    code: RoleCode.VICE_DEAN_ADMINISTRATION,
    name: RoleCode.VICE_DEAN_ADMINISTRATION,
    name_th: "รองคณบดีฝ่ายบริหาร",
    name_en: "Vice Dean for Administration",
    label: "รองคณบดีฝ่ายบริหาร",
  },
  {
    id: RoleID.VICE_DEAN_ACADEMIC,
    code: RoleCode.VICE_DEAN_ACADEMIC,
    name: RoleCode.VICE_DEAN_ACADEMIC,
    name_th: "รองคณบดีฝ่ายวิชาการ",
    name_en: "Vice Dean for Academic Affairs",
    label: "รองคณบดีฝ่ายวิชาการ",
  },
];

export const RoleObject: Record<keyof typeof RoleID, RoleOption> = {
  ADMIN: ROLE_OPTIONS[0],
  HEAD_OF_EDUCATION: ROLE_OPTIONS[1],
  ACADEMIC_OFFICER: ROLE_OPTIONS[2],
  FINANCE_OFFICER: ROLE_OPTIONS[3],
  DOCUMENT_OFFICER: ROLE_OPTIONS[4],
  HUMAN_RESOURCE: ROLE_OPTIONS[5],
  TEACHER: ROLE_OPTIONS[6],
  HEAD_OF_DEPARTMENT: ROLE_OPTIONS[7],
  VICE_DEAN_ADMINISTRATION: ROLE_OPTIONS[8],
  VICE_DEAN_ACADEMIC: ROLE_OPTIONS[9],
};

export const ROLE_ID_BY_CODE: Partial<Record<string, RoleID>> = ROLE_OPTIONS.reduce(
  (acc, role) => ({
    ...acc,
    [role.code]: role.id,
    [role.name]: role.id,
  }),
  {} as Partial<Record<string, RoleID>>,
);

function normalizeRoleLookupValue(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value).trim();
  }

  return '';
}

export function resolveRoleId(role: { id?: unknown; code?: unknown; name?: unknown } | null | undefined): RoleID | null {
  const code = normalizeRoleLookupValue(role?.code) || normalizeRoleLookupValue(role?.name);
  const roleIdFromCode = ROLE_ID_BY_CODE[code];

  if (roleIdFromCode !== undefined) {
    return roleIdFromCode;
  }

  const roleId = Number(role?.id);
  const roleOption = ROLE_OPTIONS.find((option) => Number(option.id) === roleId);

  return roleOption?.id ?? null;
}

export const SUBJECT_MANAGEMENT_EDIT_ROLE_IDS: RoleID[] = [
  RoleID.ACADEMIC_OFFICER,
  RoleID.HEAD_OF_EDUCATION,
];

// Master Data : รายวิชา — admins plus the academic workload managers.
export const SUBJECT_MASTER_DATA_ROLE_IDS: RoleID[] = [
  RoleID.ADMIN,
  ...SUBJECT_MANAGEMENT_EDIT_ROLE_IDS,
];

export const SUBJECT_MANAGEMENT_VIEW_ONLY_ROLE_IDS: RoleID[] = [];

export const SUBJECT_MANAGEMENT_ROLE_IDS: RoleID[] = [
  ...SUBJECT_MANAGEMENT_EDIT_ROLE_IDS,
  ...SUBJECT_MANAGEMENT_VIEW_ONLY_ROLE_IDS,
];

export const SUBJECT_SCHEDULE_ROLE_IDS: RoleID[] = [
  ...SUBJECT_MANAGEMENT_EDIT_ROLE_IDS,
  RoleID.HEAD_OF_EDUCATION,
  RoleID.TEACHER,
  RoleID.HEAD_OF_DEPARTMENT,
  RoleID.VICE_DEAN_ACADEMIC,
  RoleID.VICE_DEAN_ADMINISTRATION,
];

export const SUBJECT_SCHEDULE_REVIEW_ROLE_IDS: RoleID[] = [
  RoleID.HEAD_OF_EDUCATION,
  RoleID.TEACHER,
  RoleID.HEAD_OF_DEPARTMENT,
  RoleID.VICE_DEAN_ACADEMIC,
  RoleID.VICE_DEAN_ADMINISTRATION,
];

export const SCHEDULE_CORRECTION_REQUEST_REVIEW_ROLE_IDS: RoleID[] = [
  RoleID.VICE_DEAN_ACADEMIC,
];

export const TEACH_COMPENSATION_ROLE_IDS: RoleID[] = [
  // RoleID.HUMAN_RESOURCE,
  RoleID.TEACHER,
  RoleID.HEAD_OF_DEPARTMENT,
  RoleID.VICE_DEAN_ACADEMIC,
  RoleID.VICE_DEAN_ADMINISTRATION,
];

export const TEACH_COMPENSATION_REVIEW_ROLE_IDS: RoleID[] = [
  RoleID.VICE_DEAN_ACADEMIC,
];

export const COMPENSATION_SUMMARY_ROLE_IDS: RoleID[] = [
  RoleID.TEACHER,
  RoleID.HEAD_OF_DEPARTMENT,
  RoleID.VICE_DEAN_ACADEMIC,
  RoleID.VICE_DEAN_ADMINISTRATION,
];

export const ERP_LEAVE_IMPORT_ROLE_IDS: RoleID[] = [
  RoleID.HUMAN_RESOURCE,
];

export const TEACH_COMPENSATION_REPORT_ROLE_IDS: RoleID[] = [
  RoleID.HEAD_OF_EDUCATION,
  RoleID.DOCUMENT_OFFICER,
];

export const OVERLOAD_MEMO_ADMIN_ROLE_IDS: RoleID[] = [
  RoleID.ADMIN,
];

export const OVERLOAD_MEMO_REVIEW_ROLE_IDS: RoleID[] = [
  RoleID.HEAD_OF_EDUCATION,
  RoleID.VICE_DEAN_ACADEMIC,
];

export const TEACH_COMPENSATION_MANAGEMENT_ROLE_IDS: RoleID[] = Array.from(
  new Set([
    ...TEACH_COMPENSATION_ROLE_IDS,
    ...TEACH_COMPENSATION_REVIEW_ROLE_IDS,
    ...TEACH_COMPENSATION_REPORT_ROLE_IDS,
    ...COMPENSATION_SUMMARY_ROLE_IDS,
    ...ERP_LEAVE_IMPORT_ROLE_IDS,
    ...OVERLOAD_MEMO_REVIEW_ROLE_IDS,
  ]),
);

export const UNAVAILABLE_FEATURE_ROLE_IDS: RoleID[] = [
  RoleID.HUMAN_RESOURCE,
];

// Disbursement Document Certification (ขั้นตอนการรับรองเอกสารส่งเบิก) — v1.2
// re-anchored on the customer UAM (r21/r22, จัดการเอกสารส่งเบิก).
// r22 ตรวจสอบและอนุมัติเอกสารส่งเบิก — UV x5 (all six VE roles minus สารบัญ).
export const DISBURSEMENT_CERTIFY_ROLE_IDS: RoleID[] = [
  RoleID.TEACHER,
  RoleID.HEAD_OF_DEPARTMENT,
  RoleID.FINANCE_OFFICER,
  RoleID.VICE_DEAN_ADMINISTRATION,
];

export const DISBURSEMENT_DOCUMENT_OFFICER_ROLE_IDS: RoleID[] = [
  RoleID.DOCUMENT_OFFICER,
];

// Vice Dean Academic reviews the teacher's schedule-issue revision branch (A1).
export const DISBURSEMENT_REVISION_REVIEW_ROLE_IDS: RoleID[] = [
  RoleID.VICE_DEAN_ACADEMIC,
];

// r21 ข้อมูลเอกสารส่งเบิก — VE x6: การเงิน, สารบัญ, อาจารย์, หัวหน้าสาขาวิชา,
// รองวิชาการ, รองบริหาร. Row scoping (own / own-dept / all) is enforced
// server-side by DisbursementCertificationPolicy.scope — this array only
// gates route/menu visibility.
export const DISBURSEMENT_DOCUMENT_VIEW_ROLE_IDS: RoleID[] = [
  RoleID.FINANCE_OFFICER,
  RoleID.DOCUMENT_OFFICER,
  RoleID.TEACHER,
  RoleID.HEAD_OF_DEPARTMENT,
  RoleID.VICE_DEAN_ACADEMIC,
  RoleID.VICE_DEAN_ADMINISTRATION,
];

export const DISBURSEMENT_MANAGEMENT_ROLE_IDS: RoleID[] = Array.from(
  new Set([
    ...DISBURSEMENT_CERTIFY_ROLE_IDS,
    ...DISBURSEMENT_DOCUMENT_OFFICER_ROLE_IDS,
    ...DISBURSEMENT_REVISION_REVIEW_ROLE_IDS,
    ...DISBURSEMENT_DOCUMENT_VIEW_ROLE_IDS,
  ]),
);
